/**
 * Add or edit a sales or purchase invoice: who, the lines, discount and tax.
 *
 * The totals shown here are a preview - the server works them out again
 * from the lines and that is the figure that is kept.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import DialogShell, { FieldGrid } from "./DialogShell";
import { Field, Pick, Section, asOptions } from "./Fields";
import PartyPicker from "./PartyPicker";
import { api, problemOf, today, money, docNo, CURRENCIES, LOOKUPS } from "./api";

const line = () => ({ description: "", qty: "1", price: "" });

const blank = (type) => ({
  type,
  date: today(),
  due_date: "",
  currency: type === "sales" ? "USD" : "CNY",
  party_type: type === "sales" ? "customer" : "vendor",
  party_id: null,
  party_name: "",
  discount: "",
  tax_rate: "",
  reference: "",
  notes: "",
  items: [line()],
});

/** Same arithmetic as the server, for the preview under the lines. */
export function invoiceTotals(form) {
  const subtotal = form.items.reduce(
    (s, it) => s + Math.round((Number(it.qty) || 0) * (Number(it.price) || 0) * 100) / 100,
    0
  );
  const discount = Math.min(Number(form.discount) || 0, subtotal);
  const tax =
    Math.round((((subtotal - discount) * (Number(form.tax_rate) || 0)) / 100) * 100) / 100;
  return { subtotal, discount, tax, total: subtotal - discount + tax };
}

function InvoiceDialog({ open, type, invoiceId, lookups, onClose, onSaved }) {
  const [form, setForm] = useState(blank(type));
  const [number, setNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setNumber(null);
    setForm(blank(type));

    if (invoiceId) {
      api
        .get(`/invoices/${invoiceId}`)
        .then((inv) => {
          setNumber(inv.number);
          setForm({
            ...blank(type),
            ...inv,
            due_date: inv.due_date || "",
            discount: Number(inv.discount) ? String(Number(inv.discount)) : "",
            tax_rate: Number(inv.tax_rate) ? String(Number(inv.tax_rate)) : "",
            items: (inv.items || []).map((it) => ({
              description: it.description,
              qty: String(Number(it.qty)),
              price: String(Number(it.price)),
            })),
          });
        })
        .catch((e) => setError(problemOf(e, "Could not load the invoice")));
    }
  }, [open, invoiceId, type]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setItem = (i, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) }));

  const totals = invoiceTotals(form);

  const save = async () => {
    const items = form.items.filter((it) => String(it.description).trim() !== "");
    if (!items.length) return setError("Add at least one line with a description");
    if (items.some((it) => !(Number(it.qty) > 0))) return setError("Every line needs a quantity");

    setSaving(true);
    setError("");
    const body = {
      ...form,
      due_date: form.due_date || null,
      discount: Number(form.discount) || 0,
      tax_rate: Number(form.tax_rate) || 0,
      items: items.map((it) => ({ ...it, qty: Number(it.qty), price: Number(it.price) || 0 })),
    };

    try {
      if (invoiceId) await api.put(`/invoices/${invoiceId}`, body);
      else await api.post("/invoices", body);
      setSaving(false);
      onSaved();
      onClose();
    } catch (e) {
      setSaving(false);
      setError(problemOf(e, "Could not save the invoice"));
    }
    return null;
  };

  const name =
    type === "sales" ? "Sales Invoice · فاتورة مبيعات" : "Purchase Invoice · فاتورة مشتريات";
  const title = `${invoiceId ? "Edit" : "New"} ${name}${number ? ` ${docNo(type, number)}` : ""}`;

  const sumRow = (label, value, strong) => (
    <SuiBox display="flex" justifyContent="space-between" gap={4}>
      <SuiTypography sx={{ fontSize: 14, opacity: 0.75 }}>{label}</SuiTypography>
      <SuiTypography sx={{ fontSize: strong ? 17 : 14, fontWeight: strong ? 800 : 600 }}>
        {`${money(value)} ${form.currency}`}
      </SuiTypography>
    </SuiBox>
  );

  const lineSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr 1fr", md: "minmax(0, 4fr) 1fr 1.3fr 1.3fr 40px" },
    columnGap: 1.5,
    alignItems: "start",
  };

  return (
    <DialogShell
      open={open}
      title={title}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      maxWidth="lg"
    >
      <Section title="Invoice · الفاتورة">
        <FieldGrid columns={4}>
          <Field
            label="Date · التاريخ"
            type="date"
            value={form.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          <Field
            label="Due date · الاستحقاق"
            type="date"
            value={form.due_date}
            onChange={(e) => set({ due_date: e.target.value })}
          />
          <Pick
            label="Currency · العملة"
            options={asOptions(CURRENCIES)}
            value={form.currency}
            onChange={(id) => set({ currency: id || form.currency })}
            getLabel={(c) => c.name}
          />
          <Field
            label="Reference · المرجع"
            placeholder="Optional"
            value={form.reference || ""}
            onChange={(e) => set({ reference: e.target.value })}
          />
        </FieldGrid>
      </Section>

      <Section title={type === "sales" ? "Customer · العميل" : "Supplier · المورد"}>
        <SuiBox maxWidth={{ md: "50%" }}>
          <PartyPicker value={form} onChange={(p) => set(p)} parties={lookups.parties} />
        </SuiBox>
      </Section>

      <Section title="Lines · البنود">
        {form.items.map((it, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SuiBox key={i} sx={lineSx}>
            <Field
              label={i === 0 ? "Description · البيان" : ""}
              placeholder="What was sold / bought"
              inputProps={{ "aria-label": "Description" }}
              value={it.description}
              onChange={(e) => setItem(i, { description: e.target.value })}
            />
            <Field
              label={i === 0 ? "Qty · الكمية" : ""}
              type="number"
              inputProps={{ "aria-label": "Qty" }}
              value={it.qty}
              onChange={(e) => setItem(i, { qty: e.target.value })}
            />
            <Field
              label={i === 0 ? "Price · السعر" : ""}
              type="number"
              placeholder="0.00"
              inputProps={{ "aria-label": "Price" }}
              value={it.price}
              onChange={(e) => setItem(i, { price: e.target.value })}
            />
            <Field
              label={i === 0 ? "Total · المجموع" : ""}
              value={money((Number(it.qty) || 0) * (Number(it.price) || 0))}
              InputProps={{ readOnly: true }}
            />
            <IconButton
              title="Remove line"
              disabled={form.items.length === 1}
              onClick={() => set({ items: form.items.filter((_, j) => j !== i) })}
              sx={{ mt: 1 }}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </SuiBox>
        ))}
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => set({ items: [...form.items, line()] })}
        >
          + Add line · إضافة بند
        </Button>
      </Section>

      <SuiBox
        mt={3}
        display="flex"
        flexWrap="wrap"
        gap={3}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <SuiBox flex="1 1 320px">
          <FieldGrid>
            <Field
              label="Discount · الخصم"
              type="number"
              placeholder="0.00"
              value={form.discount}
              onChange={(e) => set({ discount: e.target.value })}
              InputProps={{
                endAdornment: <InputAdornment position="end">{form.currency}</InputAdornment>,
              }}
            />
            <Field
              label="Tax · الضريبة"
              type="number"
              placeholder="0"
              value={form.tax_rate}
              onChange={(e) => set({ tax_rate: e.target.value })}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </FieldGrid>
          <Field
            label="Notes · ملاحظات"
            placeholder="Optional"
            value={form.notes || ""}
            onChange={(e) => set({ notes: e.target.value })}
            multiline
            minRows={2}
          />
        </SuiBox>
        <SuiBox
          display="flex"
          flexDirection="column"
          gap={0.75}
          minWidth={280}
          p={2}
          sx={({ palette, borders, functions: { rgba } }) => ({
            backgroundColor: rgba(palette.primary.main, 0.06),
            border: `1px solid ${rgba(palette.primary.main, 0.25)}`,
            borderRadius: borders.borderRadius.lg,
          })}
        >
          {sumRow("Subtotal · المجموع الفرعي", totals.subtotal)}
          {totals.discount > 0 && sumRow("Discount · الخصم", -totals.discount)}
          {totals.tax > 0 && sumRow(`Tax · الضريبة ${Number(form.tax_rate)}%`, totals.tax)}
          {sumRow("Total · الإجمالي", totals.total, true)}
        </SuiBox>
      </SuiBox>
    </DialogShell>
  );
}

InvoiceDialog.defaultProps = { invoiceId: null };

InvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(["sales", "purchase"]).isRequired,
  invoiceId: PropTypes.number,
  lookups: LOOKUPS.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default InvoiceDialog;
